import BillingTransferNoAccounting from "./Forms/BillingTransferNoAccounting";
import { useSetupFooter } from "views/Setup/context/SetupFooterContext";
import AccountingSettingsForm from "./Forms/AccountingSettingsForm";
import useAccountingApi from "../../hooks/useAccountingApi";
import { useLazyQuery, useMutation } from "@apollo/client";
import useFooterStore from "views/hooks/useFooterStore";
import BillingTransfer from "./Forms/BillingTransfer";
import { Col, Form, notification, Row } from "antd";
import { clientNeoFact } from "@neonumy/api-clients";
import useCoreStore from "views/hooks/useCoreStore";
import { getFormError } from "utils/getFormError";
import { objectFilter } from "utils/objectFilter";
import { useTranslation } from "react-i18next";
import { useForm } from "antd/lib/form/Form";
import { generateFields } from "./utils";
import { useEffect } from "react";
import Loading from "components/Loading";
import {
  UPDATE_ACCOUNTANT_TRANSFER,
  GET_ACCOUNTANT_TRANSFER_DEFAULTS,
} from "queries/neoFact/settings";

interface TransfersProps {
  nextButton?: () => void;
  prevButton?: () => void;
  isSetup?: boolean;
}

interface AccountParametersFormValues {
  prefixClientAccount: string;
  prefixProviderAccount: string;
  collectiveClientAccount: string;
  collectiveProviderAccount: string;
  generalAccountsLength: number;
  thirdPartiesAccountsLength: number;
}

export default function Accounting(props: TransfersProps) {
  const { t } = useTranslation();
  const { nextButton, isSetup } = props;
  const [form] = useForm();
  const [accountingSettingsForm] = Form.useForm<AccountParametersFormValues>();

  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const saveLoading = useFooterStore(state => state.saveLoading);
  const companySelected = useCoreStore(state => state.companySelected);
  const companyData = useCoreStore(state => state.companyData);
  const hasNotAccountingFirm = companyData?.hasNotAccountingFirm;

  const {
    getCompanyPlatform,
    refetchCompanyPlatform,
    getCompanyPlatformLoading,
    updateCompanySettingsCentralApi,
  } = useAccountingApi({});

  const [
    getTransfer,
    { refetch: refetchGetTransfer, loading: getTransferLoading, data: getTransferData },
  ] = useLazyQuery<GetAccountantTransferDefaultsResponse, GetAccountantTransferDefaultsVars>(
    GET_ACCOUNTANT_TRANSFER_DEFAULTS,
    {
      client: clientNeoFact,
    },
  );

  useEffect(() => {
    const defaults = getTransferData?.getAccountantTransferDefaults;
    if (!defaults) return;
    form.setFields(generateFields(defaults, hasNotAccountingFirm ?? false));
  }, [getTransferData, hasNotAccountingFirm]);

  const [updateTransfer, { loading }] = useMutation<
    UpdateAccountantTransferResponse,
    UpdateAccountantTransferVars
  >(UPDATE_ACCOUNTANT_TRANSFER, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (companySelected?.id) {
      getTransfer({
        variables: {
          company: companySelected?.id,
        },
      });
      getCompanyPlatform({
        variables: {
          id: companySelected?.id,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (handleOkSaveButton) {
      handleOk();
    }
  }, [handleOkSaveButton]);

  const handleOk = () => {
    form.submit();
    setSaveLoading(true);
  };

  useSetupFooter(isSetup ? { onSave: handleOk, loading } : null);

  const onFinish = async (values: Record<string, any>) => {
    setSaveLoading(true);

    try {
      const transferResult = await updateTransfer({
        variables: {
          input: {
            companyId: companySelected?.id,
            sellBookCode: hasNotAccountingFirm ? values.sellBookCode : values.sellBookCode?.value,
            clientCounterpart: hasNotAccountingFirm
              ? values.clientCounterpartAccount
              : values.clientCounterpartAccount?.value,
            advancePaymentIssuedCounterpart: hasNotAccountingFirm
              ? values.advancePaymentIssuedCounterpart
              : values.advancePaymentIssuedCounterpart?.value,
            advancePaymentReceivedCounterpart: hasNotAccountingFirm
              ? values.advancePaymentReceivedCounterpart
              : values.advancePaymentReceivedCounterpart?.value,
            advancePaymentIssuedTvaCounterpart: hasNotAccountingFirm
              ? values.advancePaymentIssuedTvaCounterpart
              : values.advancePaymentIssuedTvaCounterpart?.value,
            advancePaymentReceivedTvaCounterpart: hasNotAccountingFirm
              ? values.advancePaymentReceivedTvaCounterpart
              : values.advancePaymentReceivedTvaCounterpart?.value,
          },
        },
      });

      const transferErrors = transferResult.data?.updateAccountantTransferDefaults?.errors || [];

      if (transferErrors.length > 0) {
        const fieldsData = getFormError(transferErrors, values);
        form.setFields(fieldsData);
        notification.error({
          message: t("Error"),
          description: t("Please check the form for errors"),
          placement: "bottomLeft",
        });
        return;
      }

      const accountingsSettingsValues = accountingSettingsForm.getFieldsValue(true);

      const parametersResponse = await updateCompanySettingsCentralApi({
        variables: {
          input: {
            id: companySelected?.id,
            ...objectFilter(
              {
                prefixClientAccount: accountingsSettingsValues.prefixClientAccount,
                prefixProviderAccount: accountingsSettingsValues.prefixProviderAccount,
                collectiveClientAccount: accountingsSettingsValues.collectiveClientAccount,
                collectiveProviderAccount: accountingsSettingsValues.collectiveProviderAccount,
                generalAccountsLength: accountingsSettingsValues.generalAccountsLength,
                thirdPartiesAccountsLength: accountingsSettingsValues.thirdPartiesAccountsLength,
                allowAccountCreationsInSsi: accountingsSettingsValues.allowAccountCreationsInSsi,
              },
              value => value !== null && value !== undefined,
            ),
          },
        },
      });

      const parametersErrors = parametersResponse?.data?.updateCompanySettings?.errors;
      if (parametersErrors && parametersErrors.length > 0) {
        const fieldsData = getFormError(parametersErrors, values);
        form.setFields(fieldsData);
        notification.error({
          message: t("Error"),
          description: t("Please check the form for errors"),
          placement: "bottomLeft",
        });
        return;
      }

      refetchGetTransfer();
      refetchCompanyPlatform();
      if (nextButton) nextButton();
      notification.success({
        message: t("Settings"),
        description: t("Saved Successfully"),
        placement: "bottomLeft",
      });
    } finally {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  const mainLoading = loading || saveLoading || getCompanyPlatformLoading || getTransferLoading;

  return (
    <>
      {mainLoading ? (
        <Loading />
      ) : (
        <Row gutter={[16, 16]}>
          <Col span={12}>
            {hasNotAccountingFirm && (
              <BillingTransferNoAccounting
                form={form}
                accountingSettingsForm={accountingSettingsForm}
                onFinish={onFinish}
              />
            )}
            {!hasNotAccountingFirm && <BillingTransfer form={form} onFinish={onFinish} />}
          </Col>
          <Col span={12}>
            <AccountingSettingsForm form={accountingSettingsForm} />
          </Col>
        </Row>
      )}
    </>
  );
}
